import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Flex, Button, Text } from '@chakra-ui/react';
import { Avatar } from '@kula-ai/components';
import { CheckCircle, Trash01 } from '@kula-ai/icons-core';
import { EmailComposeBlock } from './EmailComposeBlock';
import type {
  EmailComposeBlockData,
  EmailComposeGroupBlockData,
} from '../../../types';

interface EmailComposeGroupProps {
  drafts: EmailComposeBlockData[];
  requestedCount?: number;
  successfulCount?: number;
  failedCount?: number;
}

interface DraftItem {
  key: string;
  draft: EmailComposeBlockData;
  name: string;
  email?: string;
}

const keyFor = (d: EmailComposeBlockData, i: number): string =>
  d.draftId ?? d.toolCallId ?? `draft-${i}`;

// Lead with the candidate's name (who you're emailing); fall back to the
// address, then a neutral ordinal label.
const draftLabel = (d: EmailComposeBlockData, i: number): DraftItem => {
  const r = d.recipients[0];
  return {
    key: keyFor(d, i),
    draft: d,
    name: r?.name || r?.email || `Recipient ${i + 1}`,
    email: r?.name ? r.email : undefined,
  };
};

const NavRow = ({
  item,
  active,
  sent,
  onSelect,
  onRemove,
}: {
  item: DraftItem;
  active: boolean;
  sent: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) => (
  <Flex
    align="center"
    gap="8px"
    px="10px"
    py="8px"
    borderRadius="8px"
    cursor="pointer"
    bg={active ? 'primary-50' : 'transparent'}
    _hover={{ bg: active ? 'primary-50' : 'secondary-50' }}
    onClick={onSelect}
    data-test-id={`agentforge-email-nav-${item.key}`}
    aria-current={active}
  >
    <Avatar name={item.name} shape="circle" size={24} />
    <Box flex="1" minW="0">
      <Text
        fontSize="sm"
        fontWeight={active ? 'semibold' : 'medium'}
        color={active ? 'primary-700' : 'secondary-900'}
        isTruncated
      >
        {item.name}
      </Text>
      {item.email && (
        <Text fontSize="xs" color="secondary-500" isTruncated>
          {item.email}
        </Text>
      )}
    </Box>
    {sent ? (
      <Box as="span" color="pastel-2-dark" display="inline-flex" flexShrink={0}>
        <CheckCircle size={16} />
      </Box>
    ) : (
      <Box
        as="button"
        type="button"
        aria-label={`Remove ${item.name} from the batch`}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onRemove();
        }}
        color="secondary-400"
        display="inline-flex"
        flexShrink={0}
        _hover={{ color: 'pastel-1-dark' }}
        data-test-id={`agentforge-email-remove-${item.key}`}
      >
        <Trash01 size={16} />
      </Box>
    )}
  </Flex>
);

// Repeater-nav layout for the per-candidate drafts produced when several
// candidates were selected: a left recipient list (select / remove) and the
// active candidate's composer on the right. Every composer stays mounted so
// switching recipients preserves in-progress edits; only the active one shows.
// A single draft renders bare (no group chrome).
export const EmailComposeGroup = ({
  drafts,
  requestedCount,
  successfulCount,
  failedCount = 0,
}: EmailComposeGroupProps) => {
  const sendsRef = useRef<Map<string, () => void>>(new Map());
  const registerSend = useCallback((key: string, fn: (() => void) | null) => {
    if (fn) sendsRef.current.set(key, fn);
    else sendsRef.current.delete(key);
  }, []);

  const items = useMemo(() => drafts.map(draftLabel), [drafts]);

  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [sentKeys, setSentKeys] = useState<Set<string>>(
    () => new Set(items.filter((it) => it.draft.sent).map((it) => it.key))
  );
  const [activeKey, setActiveKey] = useState<string>(items[0]?.key ?? '');

  const visible = items.filter((it) => !removed.has(it.key));
  // Keep a valid active row even after the active one is removed.
  const active = visible.some((it) => it.key === activeKey)
    ? activeKey
    : (visible[0]?.key ?? '');

  const handleSent = useCallback((key: string) => {
    setSentKeys((prev) => new Set(prev).add(key));
  }, []);

  const removeDraft = (key: string) => {
    setRemoved((prev) => new Set(prev).add(key));
    sendsRef.current.delete(key);
  };

  const preparedCount = Math.max(
    successfulCount ?? drafts.length,
    drafts.length
  );
  const requested = Math.max(
    requestedCount ?? preparedCount + failedCount,
    preparedCount
  );
  const failureSummary =
    failedCount > 0
      ? `${preparedCount} of ${requested} personalized ${
          preparedCount === 1 ? 'draft is' : 'drafts are'
        } ready · ${failedCount} could not be prepared`
      : '';

  if (drafts.length <= 1 && failedCount === 0) {
    return drafts[0] ? <EmailComposeBlock data={drafts[0]} /> : null;
  }

  // A partial batch can contain only one successful draft. Keep that useful
  // composer visible, but retain the batch outcome instead of collapsing to a
  // bare card that makes the failed recipient disappear from the story.
  if (drafts.length === 1) {
    return (
      <Box data-test-id="agentforge-email-compose-group">
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color="secondary-900"
          mb="10px"
          px="4px"
        >
          {failureSummary}
        </Text>
        <EmailComposeBlock data={drafts[0]} />
      </Box>
    );
  }

  const total = visible.length;
  // Removing the last recipient previously left the batch chrome mounted:
  // "0 personalized drafts", a disabled "Send all (0)", and two blank
  // outlined columns. Replace that invalid action surface with a compact,
  // recoverable empty state.
  if (total === 0) {
    return (
      <Flex
        align="center"
        justify="space-between"
        gap="12px"
        px="4px"
        py="4px"
        data-test-id="agentforge-email-compose-empty"
      >
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="secondary-900">
            No personalized drafts remain
          </Text>
          <Text fontSize="xs" color="secondary-500">
            Restore the drafts if you removed them by mistake.
          </Text>
        </Box>
        <Button
          variant="secondary"
          size="sm"
          flexShrink={0}
          onClick={() => setRemoved(new Set())}
        >
          Restore drafts
        </Button>
      </Flex>
    );
  }

  const sentCount = visible.filter((it) => sentKeys.has(it.key)).length;
  const allSent = sentCount >= total;

  const sendAll = () => {
    visible.forEach((it) => {
      if (!sentKeys.has(it.key)) sendsRef.current.get(it.key)?.();
    });
  };

  return (
    <Box data-test-id="agentforge-email-compose-group">
      <Flex align="center" justify="space-between" mb="10px" px="4px" gap="8px">
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="secondary-900">
            {total} personalized draft{total === 1 ? '' : 's'}
            {sentCount > 0 ? ` · ${sentCount}/${total} sent` : ''}
          </Text>
          {failureSummary && (
            <Text fontSize="xs" color="secondary-500">
              {failureSummary}
            </Text>
          )}
        </Box>
        <Button
          variant="primary"
          size="sm"
          isDisabled={allSent}
          onClick={sendAll}
        >
          {allSent ? 'All sent' : `Send all (${total - sentCount})`}
        </Button>
      </Flex>

      <Flex
        direction={{ base: 'column', lg: 'row' }}
        gap="12px"
        align="stretch"
      >
        <Flex
          direction="column"
          gap="2px"
          flexShrink={0}
          w={{ base: '100%', lg: '220px' }}
          p="6px"
          border="1px solid"
          borderColor="border-color"
          borderRadius="10px"
          bg="base-white"
          maxH={{ lg: '520px' }}
          overflowY={{ lg: 'auto' }}
        >
          {visible.map((it) => (
            <NavRow
              key={it.key}
              item={it}
              active={it.key === active}
              sent={sentKeys.has(it.key)}
              onSelect={() => setActiveKey(it.key)}
              onRemove={() => removeDraft(it.key)}
            />
          ))}
        </Flex>

        <Box flex="1" minW="0">
          {visible.map((it) => (
            <Box key={it.key} display={it.key === active ? 'block' : 'none'}>
              <EmailComposeBlock
                data={it.draft}
                registerSend={registerSend}
                onSent={() => handleSent(it.key)}
              />
            </Box>
          ))}
        </Box>
      </Flex>
    </Box>
  );
};

/** Registry adapter for the single structured batch block. Existing groups of
 * consecutive legacy emailCompose blocks still call EmailComposeGroup
 * directly from Message.tsx. */
export const EmailComposeGroupBlock = ({
  data,
}: {
  data: EmailComposeGroupBlockData;
}) => (
  <EmailComposeGroup
    drafts={data.drafts}
    requestedCount={data.requestedCount}
    successfulCount={data.successfulCount}
    failedCount={data.failedCount}
  />
);
